import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Trophy, Sparkles, Lock, CheckCircle } from 'lucide-react'
import type { SignupSeasonWithState } from './types'
import { formatDate, formatCurrency } from './utils'

interface UpcomingSignupsSectionProps {
  upcomingSignups: SignupSeasonWithState[]
  loading: boolean
  onOpenRegistration: (season: SignupSeasonWithState) => void
  onCancelRegistration: (season: SignupSeasonWithState) => void
}

export function UpcomingSignupsSection({
  upcomingSignups,
  loading,
  onOpenRegistration,
  onCancelRegistration,
}: UpcomingSignupsSectionProps) {
  if (!loading && upcomingSignups.length === 0) return null

  if (loading) {
    return (
      <div className="mb-8">
        <Skeleton className="h-6 w-36 mb-4" />
        <div className="grid gap-4 md:grid-cols-2">
          {[1, 2].map((i) => (
            <Card key={i}>
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-48" />
                  </div>
                  <Skeleton className="h-9 w-16 rounded-md" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="mb-8">
      <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
        <Sparkles className="h-5 w-5 text-emerald-500" />
        Open for Signup
      </h2>
      <div className="grid gap-4 md:grid-cols-2">
        {upcomingSignups.map((season) => (
          <Card
            key={season.seasonId}
            className="border-emerald-500/30 bg-gradient-to-r from-emerald-500/5 to-transparent"
          >
            <CardContent className="py-3">
              <div className="flex items-center justify-between gap-3">
                {/* Left side: Info */}
                <div className="min-w-0 flex-1 space-y-1">
                  {/* Leagues / Season - same line, wrap if needed */}
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <div className="flex items-center gap-1.5">
                      <Trophy className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                      <span className="font-medium text-sm">{season.league.name}</span>
                    </div>
                    <span className="text-muted-foreground text-sm">/</span>
                    <span className="text-sm">{season.name}</span>
                  </div>
                  {/* Date + Brackets + Fee */}
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                    <span>{formatDate(season.startDate)}</span>
                    {season.brackets.length > 0 && (
                      <>
                        <span>·</span>
                        {season.brackets.map((bracket, idx) => (
                          <span key={bracket.bracketId}>
                            {bracket.name}{idx < season.brackets.length - 1 ? ', ' : ''}
                          </span>
                        ))}
                      </>
                    )}
                    {season.joiningFee && (
                      <>
                        <span>·</span>
                        <span className="font-medium text-emerald-600 dark:text-emerald-400">
                          {formatCurrency(season.joiningFee.amount, season.joiningFee.currency)}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Right side: Action */}
                <div className="shrink-0">
                  {(() => {
                    // Already a player in this season
                    if (season.isAlreadyInSeason) {
                      return (
                        <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                          <CheckCircle className="h-4 w-4" />
                          You're in
                        </span>
                      )
                    }
                    if (season.myRegistrationStatus === 'pending') {
                      return (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onCancelRegistration(season)}
                          className="h-8 px-3 text-xs"
                        >
                          Cancel
                        </Button>
                      )
                    }
                    if (season.myRegistrationStatus === 'confirmed') {
                      return (
                        <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                          <CheckCircle className="h-4 w-4" />
                          Registered
                        </span>
                      )
                    }
                    if (season.canRegister) {
                      return (
                        <Button
                          size="sm" 
                          className="h-8 px-4 bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => onOpenRegistration(season)}
                        >
                          Register
                        </Button>
                      )
                    }
                    // Members only and user is not a member
                    return (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Lock className="h-3 w-3" />
                        Members only
                      </span>
                    )
                  })()}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}
